import { Userpilot } from 'userpilot';
import { call, takeEvery, take, select } from 'redux-saga/effects';
import { LOCATION_CHANGE } from 'connected-react-router';

import { LOGOUT, VALIDATE_TOKEN_SUCCESS } from 'js/actionCreators/authActions';
import { E2E_GEN_TEST_USER_PREFIX, E2E_GEN_TEST_USER_SUFFIX } from 'js/config/e2eTestUser';
import {
  ADD_SCRIBE_SUCCESS,
  CREATE_IMAGE_ELEMENT,
  CREATE_SCENE,
  DELETE_SCRIBE_SUCCESS,
  DUPLICATE_SELECTED_SCENES,
  LOAD_SCRIBES_SUCCESS
} from 'js/actionCreators/scribeActions';
import {
  CLOSE_USERPERSONA_MODAL,
  DISMISS_USERPERSONA_MODAL,
  SUBMIT_USERPERSONA_MODAL
} from 'js/actionCreators/marketingActions';
import { TRACK_PLAY_SCRIBE, TRACK_TEMPLATE_EDIT } from 'js/actionCreators/trackingActions';
import { GET_VIDEO_DOWNLOAD_URL_SUCCESS } from 'js/actionCreators/downloadActions';
import { DUPLICATE_SCRIBE_SUCCESS } from 'js/actionCreators/duplicateScribeActions';
import { getExperimentAndFlagsUserPropertiesForTracking } from 'js/shared/helpers/getExperimentUserPropertiesForTracking';
import { OPEN_RESOURCE_CENTER } from 'js/actionCreators/uiActions';
import isElectron from 'js/shared/helpers/isElectron';

import { isRenderer } from '../sagaHelpers/isRenderer';
import { getUser } from '../selectors';

const debug = !!import.meta.env.VITE_DEBUG_USERPILOT;
const logger = debug ? (...args) => console.log(`%c[Userpilot Debug]: `, 'color: #f75a79', ...args) : () => {};
const isE2eTestUser = email => email.includes(E2E_GEN_TEST_USER_PREFIX) && email.includes(E2E_GEN_TEST_USER_SUFFIX);

let identified;

function userpilotInitializeWithLogger(id, config) {
  Userpilot.identify(id, config);
  identified = true;
  logger(`Identified user ${id} with config`, config);
}

function userpilotTrackWithLogger(eventName, properties) {
  Userpilot.track(eventName, properties);
  logger(`Track event - ${eventName}`, properties);
}

function identifiedOnlyFn(fn) {
  return (...args) => {
    if (identified) {
      return fn.apply(null, args);
    }
  };
}

function* userpilotIdentify({ user }) {
  if (!isE2eTestUser(user.email)) {
    const experimentAndFlagsUserProperties = yield call(getExperimentAndFlagsUserPropertiesForTracking);
    const userConfig = {
      email: user.email,
      name: user.firstname,
      'Subscription Tier': user.accountType,
      'Licence Type': user.accountType,
      'Signup Date': user.signupDate,
      ...experimentAndFlagsUserProperties
    };

    yield call(userpilotInitializeWithLogger, user.id, userConfig);
  }
}

function logout() {
  identified = false;
}

function* userpilotReload({ payload }) {
  const reloadConfig = isElectron() ? { url: `https://videoscribe.co/app${payload.location.pathname}` } : undefined;
  yield call(Userpilot.reload, reloadConfig);
  logger(`Reloading Userpilot`, reloadConfig);
}

const updateScribeCount = identifiedOnlyFn(function* (action) {
  const user = yield select(getUser);
  const scribes = yield select(state => state.scribes.homepageProjects);
  yield call(userpilotInitializeWithLogger, user.id, {
    'Number of Scribes Created': scribes.length
  });

  if (action.type === ADD_SCRIBE_SUCCESS && action.scribe?.source?.type === 'blank') {
    const { scribe } = action;
    yield call(userpilotTrackWithLogger, 'Create Blank Scribe', {
      'Canvas Size': scribe.canvasSize
    });
  }
});

const trackDismissUserPersonaModal = identifiedOnlyFn(function* ({ answers }) {
  yield call(trackUserPersonaModal, answers, 'Dismissed User Persona Modal');
});

const trackSubmitUserPersonaModal = identifiedOnlyFn(function* ({ answers }) {
  yield take(CLOSE_USERPERSONA_MODAL);
  yield call(trackUserPersonaModal, answers, 'Completed User Persona Modal');
});

function* trackUserPersonaModal(answers, eventName) {
  const organization = answers.user_type === 'other' ? `other:${answers.user_type_text}` : answers.user_type;
  const role = answers.role === 'other' ? `other:${answers.role_text}` : answers.role;
  const intentions = answers.intention?.length ? answers.intention.join(';') : null;

  const hasValue = value => value !== null && value !== '';

  const eventProperties = {
    ...(hasValue(organization) && { Organization: organization }),
    ...(hasValue(intentions) && { Intentions: intentions }),
    ...(hasValue(role) && { Role: role }),
    ...(hasValue(answers.team_size) && { 'Team Size': answers.team_size }),
    ...(hasValue(answers.experience_level) && { 'Experience Level': answers.experience_level })
  };

  const userProperties = {
    ...(hasValue(organization) && { 'User Organisation': organization }),
    ...(hasValue(intentions) && { 'User Intentions': intentions }),
    ...(hasValue(role) && { 'User Role': role }),
    ...(hasValue(answers.team_size) && { 'User Team Size': answers.team_size }),
    ...(hasValue(answers.experience_level) && { 'User Experience Level': answers.experience_level })
  };

  yield call(userpilotTrackWithLogger, eventName, eventProperties);

  const user = yield select(getUser);
  yield call(userpilotInitializeWithLogger, user.id, userProperties);
}

const trackEditTemplate = identifiedOnlyFn(function* ({ title, category, triggerLocation }) {
  yield call(userpilotTrackWithLogger, 'Edit Template', {
    'Template Title': title,
    'Template Category': category,
    'Button Location': triggerLocation
  });
});

const trackAddingScene = identifiedOnlyFn(function* () {
  yield call(userpilotTrackWithLogger, 'Add Scene');
});

const trackDuplicateScene = identifiedOnlyFn(function* () {
  yield call(userpilotTrackWithLogger, 'Duplicate Scene');
});

const trackPlayScribe = identifiedOnlyFn(function* ({ button, sceneCount }) {
  if (button === 'fromStart') {
    yield call(userpilotTrackWithLogger, 'Play From Video Start', {
      'Number Of Scenes': sceneCount
    });
  }
});

const trackDownload = identifiedOnlyFn(function* ({ trackingProperties }) {
  yield call(userpilotTrackWithLogger, 'Download Succeeded', trackingProperties);
});

const trackAddImageToCanvas = identifiedOnlyFn(function* () {
  yield call(userpilotTrackWithLogger, 'Add Image To Canvas');
});

function openRescourceCenter() {
  Userpilot.trigger('Sr3G26T');
}

export default function* userpilotSagas() {
  if (isRenderer()) return;

  Userpilot.initialize(import.meta.env.VITE_USERPILOT_KEY);

  yield takeEvery(LOCATION_CHANGE, userpilotReload);
  yield takeEvery(OPEN_RESOURCE_CENTER, openRescourceCenter);
  yield takeEvery([VALIDATE_TOKEN_SUCCESS], userpilotIdentify);
  yield takeEvery(LOGOUT, logout);
  yield takeEvery(
    [LOAD_SCRIBES_SUCCESS, ADD_SCRIBE_SUCCESS, DELETE_SCRIBE_SUCCESS, DUPLICATE_SCRIBE_SUCCESS],
    updateScribeCount
  );
  yield takeEvery(DISMISS_USERPERSONA_MODAL, trackDismissUserPersonaModal);
  yield takeEvery(SUBMIT_USERPERSONA_MODAL, trackSubmitUserPersonaModal);
  yield takeEvery(TRACK_TEMPLATE_EDIT, trackEditTemplate);
  yield takeEvery(CREATE_SCENE, trackAddingScene);
  yield takeEvery(DUPLICATE_SELECTED_SCENES, trackDuplicateScene);
  yield takeEvery(TRACK_PLAY_SCRIBE, trackPlayScribe);
  yield takeEvery(GET_VIDEO_DOWNLOAD_URL_SUCCESS, trackDownload);
  yield takeEvery(CREATE_IMAGE_ELEMENT, trackAddImageToCanvas);
}
